import { useParams, usePathname } from 'next/navigation';

// Utils
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import { formatUrls, formatUrlPathname } from '@/utils/urls/formatUrl';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

// Lib
import { groupHasAccess, networkHasAccess } from '@/lib/acl/pageHasAccess';

export function formatUrlKeys<TData extends typeof networkHasAccess>(
    obj: TData,
    params: ReturnType<typeof useParams>
) {
    const modifiedObject = {} as TData;

    Object.keys(obj).forEach((url) => {
        const modifiedKey = formatUrlPathname(url as string, params);
        modifiedObject[modifiedKey] = obj[url];
    });

    return modifiedObject;
}

type ActiveProfileHasAccessParamsType = {
    activeSocialProfile: ReturnType<typeof useActiveSocialProfile>['activeSocialProfile'];
    pathname: ReturnType<typeof usePathname>;
    params: ReturnType<typeof useParams>;
};

function activeSocialProfileNoAccessPage({
    activeSocialProfile,
    pathname,
    params
}: ActiveProfileHasAccessParamsType) {
    const networkHasAccessFormatted = formatUrlKeys(networkHasAccess, params);

    if (!activeSocialProfile) return false;

    return (
        pathname in networkHasAccessFormatted &&
        !networkHasAccessFormatted[pathname].includes(activeSocialProfile.social_network_type)
    );
}

export function useProfileHasAccessPage() {
    const pathname = usePathname();
    const params = useParams();

    const { activeMultiSelectionProfiles } = useProfilesStore((state) => state);

    const { activeGroup } = useActiveGroup();
    const { activeSocialProfile } = useActiveSocialProfile();

    /**
     * Check if the active profile has access to the current page
     */
    const activeSocialProfileNoAccess = activeSocialProfileNoAccessPage({
        activeSocialProfile,
        pathname,
        params
    });

    /**
     * Check if the active group has access to the current page
     */
    const activeGroupNoAccess =
        activeGroup &&
        !activeSocialProfile &&
        !formatUrls(groupHasAccess, params).includes(pathname);

    /**
     * Check if the active multi selection profiles have access to the current page
     */
    const activeMultiSelectionProfilesNoAccess =
        activeMultiSelectionProfiles?.socialProfileIds.length && !activeSocialProfile;

    return {
        activeSocialProfileNoAccess,
        activeGroupNoAccess,
        activeMultiSelectionProfilesNoAccess
    };
}
